// Email del cliente del checkout online — UNA sola regla para los tres lados.
//
// Antes había tres: el navegador (`/^[^\s@]+@[^\s@]+\.[^\s@]+$/`), /session
// (`z.string()`, cualquier cosa) y POST /api/online-orders (`z.string().email()`).
// El 2026-10-02 un cliente de zulianisimo escribió un email con un punto justo
// antes de la arroba: el navegador y /session lo dejaron pasar, North cobró
// $84,66 y la creación de la orden lo rechazó 29 veces hasta que el intent
// expiró — cobrado y sin orden (auditoría 2026-10-07, P1-2 / Sentry REDY-33).
//
// La regla es la de zod (`z.string().email()`), y se aplica distinto según el
// momento:
//   - ANTES de cobrar (navegador y /session): un email inválido se RECHAZA —
//     el cliente todavía puede corregirlo.
//   - DESPUÉS de cobrar (POST /api/online-orders, el rescate del barrido): un
//     email inválido se DESCARTA y la orden se crea sin él. El email es
//     opcional; un pago ya capturado jamás se pierde por él.
//
// Módulo normal (no "use server"): lo importan el cliente y el servidor.
import { z } from "zod"

const strictEmail = z.string().email()

/** ¿Es un email válido? Mismo criterio que `z.string().email()`. No recorta. */
export function isValidCustomerEmail(value: string): boolean {
  return strictEmail.safeParse(value).success
}

/**
 * El email que se GUARDA: recortado si es válido; `undefined` si viene vacío,
 * inválido o no es texto. Nunca lanza.
 */
export function normalizeCustomerEmail(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return isValidCustomerEmail(trimmed) ? trimmed : undefined
}

/**
 * /session (antes de cobrar): vacío o ausente = sin email; inválido = error de
 * validación (la ruta responde 400 con `field: "customer.email"`).
 */
export const sessionCustomerEmailSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || isValidCustomerEmail(v), { message: "Invalid email address" })
  .transform((v) => (v === "" ? undefined : v))
  .optional()

/** POST /api/online-orders (después de cobrar): nunca falla; descarta lo inválido. */
export const lenientCustomerEmailSchema = z
  .unknown()
  .optional()
  .transform((v) => normalizeCustomerEmail(v))
